import React, { memo } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import { useSelector } from 'react-redux';
import { COMPLETED_PROFILE_SECTIONS_NAMES, TP } from '../../../../constants/consts';
import ImagesTab from '../components/ImagesTab';
import VideosTab from '../components/VideosTab';
import ReviewsTab from '../components/ReviewsTab';
import { EditLayout } from '../../../../components/EditComponents/EditLayout';
import { checkCompletion } from '../../../../utils/editCommon';
import { selectChosenProfileData } from '../../selectors';

const ArtistNameForm = dynamic(import('../components/ArtistNameForm'));
const ProfessionEditForm = dynamic(import('../components/ProfessionEditForm'));
const EditArtistBioTab = dynamic(import('../components/EditArtistBioTab'));
const DigitalPresenceForm = dynamic(import('../components/DigitalPresenceForm'));

const tabValues = {
  name: 'name',
  profession: 'profession',
  digital: 'digital',
  biography: 'biography',
  video: 'video',
  images: 'images',
  reviews: 'reviews',
};

export function ProfileLayout({ privacyOptions }) {
  const profile = useSelector(selectChosenProfileData());
  const completedSections = profile?.completedSections || [];

  const profileTabs = {
    [tabValues.name]: {
      value: tabValues.name,
      name: `${TP}.FN_EDIT_ARTIST_PROFILE_NAME_TAB`,
      title: `${TP}.FN_EDIT_ARTIST_PROFILE_NAME_TAB`,
      subtitle: `${TP}.FN_EDIT_ARTIST_PROFILE_NAME_SUBTITLE`,
      form: <ArtistNameForm profile={profile} goToNextTab={() => goToTab(tabValues.profession)} />,
      isFilled: checkCompletion(completedSections, COMPLETED_PROFILE_SECTIONS_NAMES.NAME),
    },
    [tabValues.profession]: {
      value: tabValues.profession,
      name: `${TP}.m_EDIT_ROSTER_LIST_PROFESSION`,
      title: `${TP}.m_EDIT_ROSTER_LIST_PROFESSION`,
      subtitle: `${TP}.FN_EDIT_ARTIST_PROFILE_PROFESSION_SUBTITLE`,
      form: <ProfessionEditForm profile={profile} goToNextTab={() => goToTab(tabValues.digital)} />,
      isFilled: checkCompletion(completedSections, COMPLETED_PROFILE_SECTIONS_NAMES.PROFESSION),
    },
    [tabValues.digital]: {
      value: tabValues.digital,
      name: `${TP}.FN_EDIT_ARTIST_PROFILE_DIGITAL_TAB`,
      title: `${TP}.FN_EDIT_ARTIST_PROFILE_DIGITAL_TAB`,
      subtitle: `${TP}.FN_EDIT_ARTIST_PROFILE_DIGITAL_SUBTITLE`,
      form: (
        <DigitalPresenceForm
          profile={profile}
          goToNextTab={() => goToTab(tabValues.biography)}
          privacyOptions={privacyOptions}
        />
      ),
      isFilled: checkCompletion(completedSections, COMPLETED_PROFILE_SECTIONS_NAMES.DIGITAL_PRESENCE),
    },
    [tabValues.biography]: {
      value: tabValues.biography,
      name: `${TP}.m_ARTIST_BIOGRAPHY`,
      form: (
        <EditArtistBioTab
          profile={profile}
          goToNextTab={() => goToTab(tabValues.images)}
          privacyOptions={privacyOptions}
          title={`${TP}.m_ARTIST_BIOGRAPHY`}
          subtitle={`${TP}.FN_EDIT_ARTIST_PROFILE_BIOGRAPHY_SUBTITLE`}
        />
      ),
      isFilled: checkCompletion(completedSections, COMPLETED_PROFILE_SECTIONS_NAMES.BIOGRAPHY),
    },
    [tabValues.images]: {
      value: tabValues.images,
      name: `${TP}.m_IMAGES`,
      title: `${TP}.m_IMAGES`,
      subtitle: `${TP}.FN_EDIT_ARTIST_PROFILE_IMAGES_SUBTITLE`,
      form: <ImagesTab goToNextTab={() => goToTab(tabValues.video)} />,
      isFilled: checkCompletion(completedSections, COMPLETED_PROFILE_SECTIONS_NAMES.MEDIA),
    },
    [tabValues.video]: {
      value: tabValues.video,
      name: `${TP}.m_VIDEO`,
      form: (
        <VideosTab
          profile={profile}
          goToNextTab={() => goToTab(tabValues.reviews)}
          title={`${TP}.m_VIDEOS`}
          subtitle={`${TP}.FN_EDIT_ARTIST_PROFILE_VIDEO_SUBTITLE`}
          privacyOptions={privacyOptions}
        />
      ),
      isFilled: checkCompletion(completedSections, COMPLETED_PROFILE_SECTIONS_NAMES.MEDIA),
    },
    [tabValues.reviews]: {
      value: tabValues.reviews,
      name: `${TP}.FN_EDIT_ARTIST_PROFILE_REVIEWS_TAB`,
      form: (
        <ReviewsTab
          profile={profile}
          goToNextTab={() => {}}
          title={`edit:${TP}.FN_EDITARTIST_ADD_REVIEW`}
          subtitle={`${TP}.FN_EDIT_ARTIST_PROFILE_REVIEWS_SUBTITLE`}
          privacyOptions={privacyOptions}
        />
      ),
      isFilled: checkCompletion(completedSections, COMPLETED_PROFILE_SECTIONS_NAMES.PUBLICATIONS),
    },
  };

  const [selectedTab, selectTab] = React.useState(profileTabs[tabValues.name]);

  const goToTab = tabName => {
    selectTab(profileTabs[tabName]);
  };

  return <EditLayout tabs={profileTabs} selectedTab={selectedTab} goToTab={goToTab} />;
}
ProfileLayout.propTypes = {
  privacyOptions: PropTypes.array,
};

ProfileLayout.defaultProps = {
  privacyOptions: [],
};

export default memo(ProfileLayout);
